import React, {useRef, useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import TextBox from "./TextBox";
import {
    TextForm2FlatText,
    getHebrewTextInfo,
    ReadHebrewText,
    localStorageSetItem,
    localStorageGetItem
} from "./misc";
import {removeNekudot,checkUnvocalizedInput, hebrewComposedOnlyOf_BKPDagesh} from "./nekudot"
import {useStats} from './StatsContext';



const ExTranslation = forwardRef(({gameParams, revealed, addHistoryEntry}, ref) => {

        const {
            useStatsUseRef,
            markExDataAsFailure,
            markExDataAsSuccess,
            pickupRandomWordAndForm,
            pickupRandomExData,
            pickupRandomText
        } = useStats();

        const textType = gameParams.choice[1];

        const textBoxRefs = [useRef(), useRef()];

        // current gameMode
        const gameMode = useRef(gameParams.mode);

        const [currentText, setCurrentText] = useState(() => {
            return JSON.parse(localStorageGetItem('currentText')) || null;
        });

        // The current dialog index if we are in a dialogues mode
        const textIndex = useRef(JSON.parse(localStorageGetItem('textIndex')) || 0);
        const textIndexSave = () => {
            localStorageSetItem('textIndex', JSON.stringify(textIndex.current));
        }

        // A flag that codes whether the recorded answer is the good answer.
        const isLastRecordingSuccess = useRef(false);

        // The speaker mode
        const exTranslationSpeechMode = JSON.parse(localStorageGetItem("exTranslationSpeechMode"))

        // Managing the stats
        // we create two useStates : one for fh and one for hf (in case we are dealing with h+f)
        // and also two for sentences (for texts that use them)
        const statsRefKeyHF = useStatsUseRef(textType, "hf");
        const statsRefKeyL = useStatsUseRef(textType, "l");
        const statsRefKeyLInit = useStatsUseRef(textType, "linit");
        const statsRefKeyFH = useStatsUseRef(textType, "fh");
        const statsRefKeyHFSentences = useStatsUseRef("sentences", "hf");
        const statsRefKeyFHSentences = useStatsUseRef("sentences", "fh");
        const statsRefKeyLSentences = useStatsUseRef("sentences", "l");
        const getStatsRefKey = () => {
            if (textType === "versions") return (gameMode.current === 'l' ? statsRefKeyLSentences : statsRefKeyHFSentences);
            if (textType === "themes") return statsRefKeyFHSentences;
            if (gameMode.current === "l") return statsRefKeyL;
            if (gameMode.current === "linit") return statsRefKeyLInit;
            if (gameMode.current === "fh") return statsRefKeyFH;
            if (gameMode.current === "hf") return statsRefKeyHF;
        }
        const getStatsLRefKey = () => {
            return textType==="words" ? statsRefKeyL : statsRefKeyLSentences
        }

        // This effect is for updating the two TextBox
        useEffect(() => {
            if (currentText === null) return;
            localStorageSetItem('currentText', JSON.stringify(currentText));
            const word = currentText.word;
            const hebrewForm = currentText.hebrewForm;
            const [hebrewText, hebrewPhonetic, frenchText, hebrewTextWithVowels] = currentText.flatText;

            const h = (gameMode.current[0] === "h" || gameMode.current[0] === "l" ? 0 : 1);
            const f = 1 - h;

            textBoxRefs[f].current.setFrenchText(frenchText)
            textBoxRefs[h].current.setHebrewText(hebrewTextWithVowels,
                hebrewPhonetic, hebrewForm ? getHebrewTextInfo(word, hebrewForm) : "")

            // Set the color of the text of the second box
            if (gameMode.current === "l") {
                if (revealed) {
                    textBoxRefs[f].current.setShow([]);
                    textBoxRefs[h].current.setShow(['*','info', "*phonetic", "vowels"])
                }
                else {
                    textBoxRefs[f].current.setShow([]);
                    textBoxRefs[h].current.setShow(['info'])
                }
                return;
            }
            if (gameMode.current[0] === "l") {
                if (revealed) {
                    textBoxRefs[f].current.setShow([]);
                    textBoxRefs[h].current.setShow(['info', "*phonetic", "vowels"])
                }
                else {
                    textBoxRefs[f].current.setShow([]);
                    textBoxRefs[h].current.setShow(['info', "vowels"])
                }
                return;
            }
            if (revealed) {
                if (f===0) textBoxRefs[f].current.setShow([]);
                else textBoxRefs[f].current.setShow(["*"]);
                if (h===0) textBoxRefs[h].current.setShow(['info', "phonetic", "vowels"])
                else textBoxRefs[h].current.setShow(["*",'*info', "*phonetic", "vowels"])
                if (h==1 && exTranslationSpeechMode["fh"]) {
                    ReadHebrewText(currentText.flatText[3])
                }
            } else if (h === 0) {
                textBoxRefs[f].current.setShow(null);
                if (exTranslationSpeechMode["hf"])
                    textBoxRefs[h].current.setShow(null)
                else
                    textBoxRefs[h].current.setShow(['info'])
            } else {
                textBoxRefs[f].current.setShow([]);
                textBoxRefs[h].current.setShow(null)
            }
        }, [currentText, revealed])

        useImperativeHandle(ref, () => ({
            getFlags: () => {
                return {
                    hasRevealPhase: true,
                    hasHistoryButton: true,
                    isReadDisabled: false,
                    isQCM: false
                }
            },
            readHebrewText: () => {
                if (currentText)
                    ReadHebrewText(currentText.flatText[3]);
            },
            isNequdotActive: () => {
                return gameMode.current[0]==='h' || gameMode.current[0] ==='l'
            },
            nequdotClick: (value) => {
                if (value) textBoxRefs[0].current.setShow(['info', "vowels"])
                else textBoxRefs[0].current.setShow(['info'])
            },
            endEx: () => {
                localStorage.removeItem('currentText');
                localStorage.removeItem('textIndex');
            },
            isRecordingDisabled: () => {
                return revealed || gameMode.current === 'hf';
            },
            isRecordingSuccess: (text) => {
                let match = null;
                [isLastRecordingSuccess.current,match] = checkUnvocalizedInput(currentText.flatText[3],text);
                return [isLastRecordingSuccess.current,match];
            },
            isEndEx: () => {
                return (currentText?.text && (textType === "versions" || textType === "themes") &&
                    currentText.text.exDataList.length - 1 === textIndex.current);
            },

            /* Main function to notify success or failure */
            notifySuccess: (flagSuccess) => {
                const statsRefKey = getStatsRefKey();
                let icon;
                if (flagSuccess) {
                    markExDataAsSuccess(statsRefKey, currentText.exData)
                    // If recording was good for french -> hebrew translation,
                    // we consider that the reading was good too ....
                    if (gameMode.current==='fh' && isLastRecordingSuccess.current) {
                        markExDataAsSuccess(getStatsLRefKey(), currentText.exData)
                    }
                    icon = "✅"
                } else {
                    markExDataAsFailure(statsRefKey, currentText.exData)
                    icon = "❌"
                }

                const h = (gameMode.current[0] === "h" || gameMode.current[0] === "l" ? 0 : 1);
                const [hebrewText, hebrewPhonetic, frenchText, hebrewTextWithVowels] = currentText.flatText;

                addHistoryEntry({
                    text1: (h === 0 ? removeNekudot(hebrewTextWithVowels) + " " + icon : icon + " " + frenchText),
                    lang1: (h === 0 ? 'h' : 'f'),
                    text2: (h === 1 ? removeNekudot(hebrewTextWithVowels) + " " + icon : icon + " " + frenchText),
                    lang2: (h === 1 ? 'h' : 'f'),
                    textWithVowels: hebrewTextWithVowels
                });
            },

            // Function to select the next question
            nextEx: (firstEx = false) => {

                if (currentText && !firstEx) return;
                isLastRecordingSuccess.current = false;

                // First of all we pick up the language if gameMode===f+h
                if (gameParams.mode === "h+f") {
                    if (Math.random() > 0.5) gameMode.current = "fh";
                    else gameMode.current = "hf";
                }
                const statsRefKey = getStatsRefKey();

                let word, hebrewForm, flatText, exData, text = null;

                // WORD CASE
                if (textType === 'words') {
                    [word, hebrewForm] = pickupRandomWordAndForm(statsRefKey, gameParams.texts);
                    exData = word.formes[hebrewForm];
                    flatText = TextForm2FlatText(word, hebrewForm);
                }

                // SENTENCE CASE
                else if (textType === 'sentences') {
                    flatText = pickupRandomExData(statsRefKey, gameParams.texts, 'exData')
                    exData = flatText
                }

                // Case versions/themes
                else {
                    // Select the text if not yet done : for now the firstEx one ! ==> stupid si révision
                    if (!currentText) {
                        text = pickupRandomText(statsRefKey, gameParams.texts);
                        textIndex.current = 0;
                    } else {
                        text = currentText.text;
                        textIndex.current += 1;
                    }
                    textIndexSave()
                    flatText = text.exDataList[textIndex.current];
                    exData = flatText;
                }

                setCurrentText({exData: exData, text: text, word: word, hebrewForm: hebrewForm, flatText: flatText});

                if (exTranslationSpeechMode["hf"] && gameMode.current === "hf") {
                    ReadHebrewText(flatText[3]);
                }
            }
        }));

        return (
            <div className={"exercice-block"}>
                <div>
                    <TextBox ref={textBoxRefs[0]}/>
                </div>
                <div>
                    <TextBox ref={textBoxRefs[1]}/>
                </div>
            </div>
        )
    }
);


const ExTranslationGetInfo = ({action = null, mode = null, choiceIndex = null, texts = null}) => {

    if (action === null) {
        return [
            {
                "title": "Trad. héb. → franç.",
                "jsname": "ExTranslation",
                "group": "hf",
                "choices": [["mots", "words"], ["phrases", "sentences"], ["textes", "versions"]],
                "WordScoreMenuItem": [["héb. → franç."]],
                "element": ExTranslation,
                "lessonsExcept": [0],
                "coeff": 1
            },
            {
                "title": "Trad. franç. → héb.",
                "jsname": "ExTranslation",
                "group": "fh",
                "choices": [["mots", "words"], ["phrases", "sentences"], ["textes", "themes"]],
                "WordScoreMenuItem": [["franç. → héb."]],
                "element": ExTranslation,
                "lessonsExcept": [0],
                "coeff": 1
            },
/*            {
                "title": "Trad. franç. ⇄ héb.",
                "jsname": "ExTranslation",
                "mode": "h+f",
                "choices": [["mots", "words"], ["phrases", "sentences"]],
                "element": ExTranslation,
                "lessonsExcept": [0],
                "noscore": true
            },*/
            {
                "title": "Lecture",
                "jsname": "ExTranslation",
                "group": "l",
                "choices": [["mots", "words"], ["phrases", "sentences"], ["textes", "versions"]],
                "WordScoreMenuItem": [["Lecture"]],
                "element": ExTranslation,
                "lessonsExcept": [0],
                "coeff": 0
            },
            {
                "title": "Initiation à la lecture",
                "jsname": "ExTranslation",
                "group": "linit",
                "choices": [["mots de 3 lettres", "words"], ["mots de moins de 4 lettres", "words"], ["mots quelconques", "words"],
                    ["mots avec les lettres אהימםנןת\u200E", "words"],
                    ["... et les lettres גדלרש \u200E", "words"],
                    ["... et les lettres וזחט \u200E", "words"],
                    ["... et les lettres סעצץק \u200E", "words"],
                    ["... et les lettres בּכּפּ \u200E", "words"],
                    ["... et les lettres בכךפף \u200E", "words"]
                ],
                "WordScoreMenuItem": [["Lect. 3- lettres"],["Lect. 4- lettres"],["Lect. quelc."]],
                "element": ExTranslation,
                "lessonsOnly": [0],
                "coeff": 1
            }
        ]
    }

    if (action === "filter") {
        if (mode !== "linit") return texts;
        // Case with specific letters
        if (choiceIndex >= 3) {
            let authorizedLetters =  " ְִ-־אהימםנןת"
            if (choiceIndex >= 4) authorizedLetters += "גדלרש"
            if (choiceIndex >= 5) authorizedLetters += "וזחט"
            if (choiceIndex >= 6) authorizedLetters += "סעצץק"
            if (choiceIndex >= 7) authorizedLetters += "בּכּפּ"
            if (choiceIndex >= 8) authorizedLetters += "בכךפף"
            console.log(authorizedLetters)
            const texts1 = texts.map(w => {
                let w1 = {...w};
                w1.formes = Object.fromEntries(Object.entries(w1.formes).filter(([key, value]) => hebrewComposedOnlyOf_BKPDagesh(value[3],authorizedLetters)))
                if (Object.keys(w1.formes).length===0) return null;
                return w1
            }).filter(w => w !==null)
            return texts1
        }
        const sizeLimit = choiceIndex===0 ? 3 : (choiceIndex===1 ? 4 : -1)
        if (sizeLimit > 0) {
            const texts1 = texts.map(w => {
                let w1 = {...w};
                w1.formes = Object.fromEntries(Object.entries(w1.formes).filter(([key, value]) => removeNekudot(value[3]).length <= sizeLimit));
                if (Object.keys(w1.formes).length===0) return null;
                return w1
            }).filter(w => w !==null)
            return texts1
        }
        else
            return texts;
    }
}

export {ExTranslation, ExTranslationGetInfo};
